import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ThemeToggle } from './ThemeToggle';

/* The frame around signing in: brand, one line about what the product is for, the form, and one
 * line about where the session lives.
 *
 * The design spec's P0 names this as undesigned, and it was — the two auth routes were a
 * `maxWidth: 420px` inline style around a bare `Card`, rendered on the page surface with no brand
 * and nothing to say what the product was. This is the mockup's arrangement: centred, 380px, brand
 * above the card, tagline, elevated card, footer note.
 *
 * **The footer line is a fact rather than reassurance copy.** `session.ts` stores the token in
 * `sessionStorage` and says why: "a token that outlives the tab is a token somebody else can find".
 * Saying so on the login screen is the only place a user learns that closing the tab signs them
 * out — otherwise they discover it by being signed out and reading it as a bug.
 *
 * **The theme toggle is here deliberately.** It lives in the app's top bar, which does not exist
 * until somebody is signed in, so without this the two screens a user meets first are the two they
 * cannot set the theme on. It is also the only control on the page that works before authentication,
 * which is a reasonable thing for a login screen to offer.
 *
 * **In `app/` rather than `shared/ui/` because it holds `ThemeToggle`**, which is an app-layer
 * component: the FSD layering runs app -> features -> shared, so a shared component importing one
 * from `app` is an upward import and ESLint's `boundaries` rule fails the build on it. `AppShell`
 * lives here for the same reason, and these two are the same kind of thing - the frame the router
 * renders a route inside.
 */

export interface AuthShellProps {
  readonly children: ReactNode;
}

export function AuthShell({ children }: AuthShellProps): ReactElement {
  const { t } = useTranslation();

  return (
    <div className="lq-auth">
      <div className="lq-auth__corner">
        <ThemeToggle />
      </div>

      {/* The wordmark, matching the shell's: "Lumiq" in the UI face and "AI" in mono. The mono is
          not decoration — it is the same treatment identifiers get everywhere in this product, and
          it keeps the mark from reading as two words of prose. */}
      <div className="lq-auth__brand">
        <span className="lq-auth__brand-name">{t('shell.brand')}</span>
        <span className="lq-auth__brand-mark">{t('shell.brandMark')}</span>
      </div>
      <p className="lq-auth__tagline">{t('auth.tagline')}</p>

      <div className="lq-auth__card">{children}</div>

      <p className="lq-auth__foot">{t('auth.deployment')}</p>
    </div>
  );
}
